import React, { useEffect, useState, useCallback } from "react";
import { Search } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { DISTRICTS, GOTRAMS } from "@/i18n";
import ArchakaCard from "@/components/ArchakaCard";
import { DiyaLoader, Toranam } from "@/components/Ornaments";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Archakas() {
  const { t } = useApp();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gotram, setGotram] = useState("all");
  const [pref, setPref] = useState("all");
  const [district, setDistrict] = useState("all");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/archakas", { params: { gotram, preference: pref, district, search } });
      setItems(data);
    } catch (e) {} finally { setLoading(false); }
  }, [gotram, pref, district, search]);

  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); }, [load]);

  return (
    <div className="parchment-texture min-h-screen">
      <div className="bg-gradient-to-r from-maroon to-saffron py-10 text-center">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white">🧎 {t("archakas_heading")}</h1>
        <p className="text-white/85 font-telugu mt-2">వైదిక వంశ పరంపర, ఆగమ నిపుణత గల అర్చకులు</p>
      </div>
      <Toranam />

      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-saffron" />
            <Input data-testid="archaka-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("search")} className="pl-9 bg-surface gold-border font-telugu" />
          </div>
          <Select value={gotram} onValueChange={setGotram}>
            <SelectTrigger data-testid="archaka-gotram-filter" className="bg-surface gold-border font-telugu"><SelectValue placeholder={t("filter_gotram")} /></SelectTrigger>
            <SelectContent><SelectItem value="all">{t("all_gotrams")}</SelectItem>{GOTRAMS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={pref} onValueChange={setPref}>
            <SelectTrigger data-testid="archaka-pref-filter" className="bg-surface gold-border font-telugu"><SelectValue placeholder={t("filter_pref")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all_prefs")}</SelectItem>
              <SelectItem value="family">👨‍👩‍👦 {t("with_family")}</SelectItem>
              <SelectItem value="bachelor">🧘 {t("bachelor")}</SelectItem>
              <SelectItem value="both">🕉️ {t("open_both")}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={district} onValueChange={setDistrict}>
            <SelectTrigger data-testid="archaka-district-filter" className="bg-surface gold-border font-telugu"><SelectValue placeholder={t("all_districts")} /></SelectTrigger>
            <SelectContent><SelectItem value="all">{t("all_districts")}</SelectItem>{DISTRICTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {loading ? <DiyaLoader /> : items.length === 0 ? (
          <div className="text-center py-20 font-telugu text-muted-foreground">అర్చకులు కనబడలేదు</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="archakas-grid">{items.map((a) => <ArchakaCard key={a.id} archaka={a} />)}</div>
        )}
      </div>
    </div>
  );
}
